import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie
} from 'recharts';
import {
  DollarSign, Truck, Gauge, Hash, Weight, MapPin, Calendar,
  AlertTriangle, CheckCircle, TrendingDown, Users, RefreshCw, Loader2,
  Sun, Moon, Target
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);

const RiskBadge = ({ level }) => {
  const cfg = {
    critical: { bg: 'bg-red-100 text-red-800 border-red-200', label: 'Critique' },
    warning: { bg: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Attention' },
    normal: { bg: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Normal' },
  };
  const c = cfg[level] || cfg.normal;
  return <Badge className={`${c.bg} text-xs`} data-testid="risk-badge">{c.label}</Badge>;
};

const KPI = ({ icon: Icon, label, value, sub, highlight }) => (
  <div className={`rounded-xl p-4 border ${highlight ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-100'}`}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`w-4 h-4 ${highlight ? 'text-red-500' : 'text-gray-400'}`} />
      <span className="text-xs text-gray-500 font-medium">{label}</span>
    </div>
    <p className={`text-xl font-bold ${highlight ? 'text-red-700' : 'text-gray-900'}`}>{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

const StatusDonut = ({ statusDistribution, total }) => {
  if (!statusDistribution?.length) return null;
  const data = statusDistribution.map(s => ({ name: s.label, value: s.count, fill: s.color }));
  return (
    <div className="flex items-center gap-4" data-testid="analytics-status-donut">
      <div className="w-[130px] h-[130px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" innerRadius={36} outerRadius={58} paddingAngle={2} dataKey="value" stroke="none" />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-col gap-1.5">
        {statusDistribution.map(s => (
          <div key={s.status} className="flex items-center gap-2 text-xs text-gray-600">
            <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
            <span>{s.label}</span>
            <span className="font-semibold text-gray-900">{s.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const DAY_SHORT = { lundi: 'L', mardi: 'M', mercredi: 'Me', jeudi: 'J', vendredi: 'V', samedi: 'S', dimanche: 'D' };

const SlotAnalysisSection = ({ slotAnalysis, terminology }) => {
  const sa = slotAnalysis;
  if (!sa?.slots?.length) return null;

  const conformColor = (rate) => rate >= 75 ? 'text-emerald-700 bg-emerald-50' : rate >= 50 ? 'text-amber-700 bg-amber-50' : 'text-red-700 bg-red-50';

  return (
    <Card className="border-gray-200" data-testid="slot-analysis-section">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
            <Target className="w-3.5 h-3.5" /> Cout & Conformite par Poste
          </p>
          {!sa.hasTruckSlots && (
            <Badge variant="outline" className="text-xs text-amber-600 border-amber-200">Planning auto (non configure)</Badge>
          )}
        </div>

        {/* Summary KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KPI icon={Hash} label="Transferts attendus" value={sa.totalExpectedRuns} />
          <KPI icon={Hash} label="Transferts realises" value={sa.totalActualRuns} sub={`${sa.totalConformityRate}% conformite`} highlight={sa.totalConformityRate < 50} />
          <KPI icon={DollarSign} label="Cout/transfert attendu" value={`${fmt(sa.avgCostPerRunExpected)}`} sub="FCFA" />
          <KPI icon={DollarSign} label="Cout/transfert reel" value={`${fmt(sa.avgCostPerRunActual)}`} sub="FCFA" highlight={sa.avgCostPerRunActual > sa.avgCostPerRunExpected * 1.3} />
        </div>

        {/* Per-slot table */}
        <div className="overflow-x-auto rounded-xl border border-gray-100">
          <table className="w-full text-sm" data-testid="slot-analysis-table">
            <thead>
              <tr className="bg-gray-50/80">
                <th className="text-left py-3 px-3 text-xs text-gray-500 font-medium">Poste</th>
                <th className="text-left py-3 px-3 text-xs text-gray-500 font-medium">Shift</th>
                <th className="text-left py-3 px-3 text-xs text-gray-500 font-medium">Jours</th>
                <th className="text-right py-3 px-3 text-xs text-gray-500 font-medium">Attendus</th>
                <th className="text-right py-3 px-3 text-xs text-gray-500 font-medium">Realises</th>
                <th className="text-right py-3 px-3 text-xs text-gray-500 font-medium">Conformite</th>
                <th className="text-right py-3 px-3 text-xs text-gray-500 font-medium">Cout/transfert</th>
                <th className="text-right py-3 px-3 text-xs text-gray-500 font-medium">Cout reel</th>
                <th className="text-right py-3 px-3 text-xs text-gray-500 font-medium">Ecart</th>
              </tr>
            </thead>
            <tbody>
              {sa.slots.map((s, i) => (
                <tr key={s.slotId || i} className="border-t border-gray-50 hover:bg-gray-50/50">
                  <td className="py-2.5 px-3">
                    <div>
                      <span className="font-medium text-gray-800 text-xs">{s.label}</span>
                      <span className="text-xs text-gray-400 ml-1">{s.vehicleType}</span>
                    </div>
                  </td>
                  <td className="py-2.5 px-3">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs ${s.shift === 'day' ? 'bg-amber-50 text-amber-700' : 'bg-indigo-50 text-indigo-700'}`}>
                      {s.shift === 'day' ? <Sun className="w-3 h-3" /> : <Moon className="w-3 h-3" />}
                      {s.shift === 'day' ? 'Jour' : 'Nuit'}
                    </span>
                  </td>
                  <td className="py-2.5 px-3">
                    <div className="flex gap-0.5">
                      {['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'].map(d => (
                        <span key={d} className={`w-5 h-5 rounded text-[10px] flex items-center justify-center ${s.daysOfWeek?.includes(d) ? 'bg-emerald-100 text-emerald-700 font-bold' : 'bg-gray-50 text-gray-300'}`}>
                          {DAY_SHORT[d]}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-right font-medium text-gray-700">{s.expectedRuns}</td>
                  <td className="py-2.5 px-3 text-right font-bold">{s.actualRuns}</td>
                  <td className="py-2.5 px-3 text-right">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${conformColor(s.conformityRate)}`}>
                      {s.conformityRate}%
                    </span>
                  </td>
                  <td className="py-2.5 px-3 text-right text-gray-600">{fmt(s.costPerRunExpected)}</td>
                  <td className="py-2.5 px-3 text-right font-medium">{fmt(s.costPerRunActual)}</td>
                  <td className="py-2.5 px-3 text-right">
                    {s.costGap !== 0 && (
                      <span className={`text-xs font-semibold ${s.costGap > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        {s.costGap > 0 ? '+' : ''}{fmt(s.costGap)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200 bg-gray-50/60">
                <td colSpan="3" className="py-2.5 px-3 text-xs font-semibold text-gray-600">TOTAL</td>
                <td className="py-2.5 px-3 text-right font-bold text-gray-700">{sa.totalExpectedRuns}</td>
                <td className="py-2.5 px-3 text-right font-bold text-gray-900">{sa.totalActualRuns}</td>
                <td className="py-2.5 px-3 text-right">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${conformColor(sa.totalConformityRate)}`}>
                    {sa.totalConformityRate}%
                  </span>
                </td>
                <td className="py-2.5 px-3 text-right font-medium text-gray-600">{fmt(sa.avgCostPerRunExpected)}</td>
                <td className="py-2.5 px-3 text-right font-bold">{fmt(sa.avgCostPerRunActual)}</td>
                <td className="py-2.5 px-3 text-right">
                  {sa.avgCostPerRunActual > sa.avgCostPerRunExpected && (
                    <span className="text-xs font-semibold text-red-600">
                      +{fmt(sa.avgCostPerRunActual - sa.avgCostPerRunExpected)}
                    </span>
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

const ContractAnalyticsTab = ({ contractId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/contract-analytics/${contractId}`);
      setData(res.data?.data || null);
    } catch (e) {
      console.error('Analytics error:', e);
      setError(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [contractId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="analytics-tab-loading">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-500">Chargement de l'analyse...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12" data-testid="analytics-tab-error">
        <p className="text-gray-500 mb-3">{error}</p>
        <Button onClick={fetchData} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="w-4 h-4" /> Reessayer
        </Button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-gray-400" data-testid="analytics-tab-empty">
        Aucune donnee disponible pour l'analyse de ce contrat.
      </div>
    );
  }

  const c = data;
  const term = c.terminology || 'operation';
  const termLabel = term.charAt(0).toUpperCase() + term.slice(1);
  const termPlural = c.kpis.totalRuns > 1 ? `${termLabel}s` : termLabel;

  const riskColors = { critical: '#ef4444', warning: '#f59e0b', normal: '#10b981' };

  return (
    <div className="space-y-6" data-testid="contract-analytics-tab">
      {/* Risk banner */}
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-xl ${c.riskLevel === 'critical' ? 'bg-red-100' : c.riskLevel === 'warning' ? 'bg-amber-100' : 'bg-emerald-100'}`}>
          {c.riskLevel === 'critical' ? <AlertTriangle className="w-5 h-5 text-red-600" /> :
           c.riskLevel === 'warning' ? <AlertTriangle className="w-5 h-5 text-amber-600" /> :
           <CheckCircle className="w-5 h-5 text-emerald-600" />}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900">Evaluation du risque</span>
            <RiskBadge level={c.riskLevel} />
          </div>
          {c.riskReasons?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-1">
              {c.riskReasons.map((r, i) => (
                <span key={i} className="text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full">{r}</span>
              ))}
            </div>
          )}
        </div>
        <Button onClick={fetchData} variant="ghost" size="sm" className="ml-auto gap-1" data-testid="refresh-analytics-btn">
          <RefreshCw className="w-3.5 h-3.5" /> Actualiser
        </Button>
      </div>

      {/* Financial KPIs (ADMIN ONLY) */}
      <Card className="border-gray-200">
        <CardContent className="p-5 space-y-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
            <DollarSign className="w-3.5 h-3.5" /> Economie du contrat
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <KPI icon={DollarSign} label="Cout mensuel" value={`${fmt(c.financial.monthlyCost)}`} sub="FCFA / mois" />
            <KPI icon={Hash} label={`Cout / ${term}`} value={`${fmt(c.financial.costPerRun)}`} sub="FCFA" />
            <KPI icon={Weight} label="Cout / tonne" value={`${fmt(c.financial.costPerTon)}`} sub="FCFA / T" />
            <KPI icon={TrendingDown} label="Spot moy. / T" value={`${fmt(c.financial.spotAvgPricePerTon)}`} sub="FCFA / T" />
            <KPI
              icon={TrendingDown}
              label="Contrat vs Spot"
              value={`${c.financial.contractVsSpot > 0 ? '+' : ''}${c.financial.contractVsSpot}%`}
              highlight={c.financial.contractVsSpot > 20}
              sub={c.financial.contractVsSpot > 0 ? 'Plus cher que spot' : 'Moins cher que spot'}
            />
          </div>
        </CardContent>
      </Card>

      {/* Fleet & Utilization (ADMIN ONLY) */}
      <Card className="border-gray-200">
        <CardContent className="p-5 space-y-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
            <Truck className="w-3.5 h-3.5" /> Flotte & Utilisation
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <KPI icon={Truck} label="Camions contractes" value={c.fleet.contractedTrucks} sub={c.fleet.contractedItems?.map(fi => `${fi.label} x${fi.quantity}`).join(', ')} />
            <KPI icon={Truck} label="Plaques actives" value={c.fleet.activePlates} />
            <KPI icon={Gauge} label="Utilisation truck-jours" value={`${c.fleet.truckDayUtilization}%`} highlight={c.fleet.truckDayUtilization < 30} />
            <KPI icon={Calendar} label="Taux activite" value={`${c.fleet.activityRate}%`} sub={`${c.fleet.activeDays} / ${c.fleet.workingDays} jours`} highlight={c.fleet.activityRate < 30} />
            <KPI icon={MapPin} label="Km total" value={c.kpis.totalKm > 0 ? `${fmt(c.kpis.totalKm)} km` : '--'} sub={c.kpis.avgKmPerRun > 0 ? `~${c.kpis.avgKmPerRun} km/op` : ''} />
          </div>
        </CardContent>
      </Card>

      {/* Performance KPIs (CLIENT-VISIBLE) */}

      {/* === SLOT ANALYSIS (Cout & Conformite par Poste) === */}
      {c.slotAnalysis && (
        <SlotAnalysisSection slotAnalysis={c.slotAnalysis} terminology={c.terminology} />
      )}

      <Card className="border-gray-200">
        <CardContent className="p-5 space-y-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
            <Gauge className="w-3.5 h-3.5" /> Performance opérationnelle
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <KPI icon={Hash} label={`Total ${termPlural.toLowerCase()}`} value={c.kpis.totalRuns} />
            <KPI icon={Hash} label="Terminees" value={c.kpis.completedRuns} sub={`${c.kpis.completionRate}%`} />
            <KPI icon={Weight} label="Poids net total" value={`${fmt(c.kpis.totalWeight)} T`} sub={`Moy: ${c.kpis.avgWeight} T`} />
            <KPI icon={Hash} label="Ops / jour actif" value={c.kpis.runsPerActiveDay} />
            <KPI icon={Weight} label="Tonnes / jour actif" value={`${c.kpis.tonsPerActiveDay} T`} />
          </div>
        </CardContent>
      </Card>

      {/* Charts row: Status + Weekly volume */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status distribution */}
        <Card className="border-gray-200">
          <CardContent className="p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Repartition des statuts</p>
            <StatusDonut statusDistribution={c.statusDistribution} total={c.kpis.totalRuns} />
          </CardContent>
        </Card>

        {/* Volume by week */}
        {c.volumeByWeek?.length > 0 && (
          <Card className="border-gray-200">
            <CardContent className="p-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Volume par semaine</p>
              <div className="h-[170px]" data-testid="analytics-weekly-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={c.volumeByWeek} margin={{ left: 0, right: 0, top: 5, bottom: 0 }}>
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="#9ca3af" />
                    <YAxis tick={{ fontSize: 10 }} stroke="#9ca3af" unit=" T" />
                    <Tooltip formatter={(val, name) => [name === 'weight' ? `${val} T` : val, name === 'weight' ? 'Poids' : 'Operations']} />
                    <Bar dataKey="weight" name="weight" radius={[6, 6, 0, 0]} barSize={28}>
                      {c.volumeByWeek.map((_, i) => (
                        <Cell key={i} fill={i === c.volumeByWeek.length - 1 ? '#10b981' : '#e2e8f0'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Operations by truck */}
      {c.operationsByTruck?.length > 0 && (
        <Card className="border-gray-200">
          <CardContent className="p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <Truck className="w-3.5 h-3.5" /> Opérations par camion
            </p>
            <div className="overflow-x-auto rounded-xl border border-gray-100">
              <table className="w-full text-sm" data-testid="analytics-ops-by-truck">
                <thead>
                  <tr className="bg-gray-50/80">
                    <th className="text-left py-3 px-4 text-xs text-gray-500 font-medium">Plaque</th>
                    <th className="text-left py-3 px-4 text-xs text-gray-500 font-medium">Type</th>
                    <th className="text-right py-3 px-4 text-xs text-gray-500 font-medium">Nb ops</th>
                    <th className="text-right py-3 px-4 text-xs text-gray-500 font-medium">Poids total</th>
                    <th className="text-right py-3 px-4 text-xs text-gray-500 font-medium">Poids moy.</th>
                  </tr>
                </thead>
                <tbody>
                  {c.operationsByTruck.map(t => (
                    <tr key={t.plate} className="border-t border-gray-50 hover:bg-gray-50/50">
                      <td className="py-2.5 px-4 font-mono text-xs font-semibold text-gray-800">{t.plate}</td>
                      <td className="py-2.5 px-4 text-gray-600 capitalize">{t.vehicleType}</td>
                      <td className="py-2.5 px-4 text-right font-bold">{t.opsCount}</td>
                      <td className="py-2.5 px-4 text-right">{t.totalWeight} T</td>
                      <td className="py-2.5 px-4 text-right text-gray-500">{t.avgWeight} T</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {c.noPlateCount > 0 && (
              <div className="flex items-center gap-2 mt-2 text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
                <AlertTriangle className="w-3.5 h-3.5" />
                {c.noPlateCount} {termPlural.toLowerCase()} sans vehicule assigne
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Driver performance */}
      {c.driverPerformance?.length > 0 && (
        <Card className="border-gray-200">
          <CardContent className="p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Performance par chauffeur
            </p>
            <div className="overflow-x-auto rounded-xl border border-gray-100">
              <table className="w-full text-sm" data-testid="analytics-driver-perf">
                <thead>
                  <tr className="bg-gray-50/80">
                    <th className="text-left py-3 px-4 text-xs text-gray-500 font-medium">Chauffeur</th>
                    <th className="text-right py-3 px-4 text-xs text-gray-500 font-medium">Nb {termPlural.toLowerCase()}</th>
                    <th className="text-right py-3 px-4 text-xs text-gray-500 font-medium">Poids total</th>
                  </tr>
                </thead>
                <tbody>
                  {c.driverPerformance.map(d => (
                    <tr key={d.driverName} className="border-t border-gray-50 hover:bg-gray-50/50">
                      <td className="py-2.5 px-4 font-medium text-gray-800">{d.driverName}</td>
                      <td className="py-2.5 px-4 text-right font-bold">{d.opsCount}</td>
                      <td className="py-2.5 px-4 text-right">{d.totalWeight} T</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ContractAnalyticsTab;
